'use client';

import { FC, useEffect, useRef } from 'react';
import { EVENT_CONSENT_CHANGED } from '../utils/event-names';
import { ConsentCategory } from '../types';
import { useIsTrackerReady } from '../hooks';
import { consentListsAreEqual, createConsentCollectionFromConsentList, getUtag } from '../core';
import { clientCanReceiveMessages, sendClientMessage } from '@tuic/client-messaging';
import { logger } from '../utils';
export const ClientMessageSender: FC = () => {
  let ready = useIsTrackerReady();
  let categories = useRef<ConsentCategory[] | undefined>(undefined);
  let isLocal = process.env.NEXT_PUBLIC_APP_ENV === 'local';

  // This function is used to handle the consent change event.
  let handleConsentChange = () => {
    // Initially categories.current is undefined, so we need to check if the value was set by the tracker. Until then, we are not ready to send messages.
    if (typeof categories.current === 'undefined') {
      return;
    }

    // Get the updated categories from the tracker & check if they are equal to the previous ones ...
    let updatedCategories: ConsentCategory[] = [];
    try {
      updatedCategories = getUtag(window)?.gdpr.getSelectedCategories() ?? [];
    } catch (e) {
      console.error('Could not set selected categories on utag.', e);
      return;
    }
    let equal = consentListsAreEqual(categories.current, updatedCategories);

    // ... if they are equal, we don't need to send a message.
    if (equal) {
      return;
    }

    // Let's update the categories and send a message to the client.
    categories.current = updatedCategories;
    let collection = createConsentCollectionFromConsentList(updatedCategories);
    sendClientMessage({
      type: 'consentChanged',
      payload: {
        categories: collection
      }
    });
  };

  // This effect is used to set the initial categories when the tracker is ready.
  useEffect(() => {
    if (!ready) {
      return;
    }
    try {
      categories.current = getUtag(window)?.gdpr.getSelectedCategories() ?? [];
    } catch (e) {
      console.error('Could not get selected categories from utag.', e);
      categories.current = [];
    }
  }, [ready]);

  // This effect is used to set up the message listener when the component is mounted.
  useEffect(() => {
    // If the client cannot receive messages, we don't need to set up the listener.
    if (!clientCanReceiveMessages()) {
      logger.log('Client messaging was disabled because the client cannot receive messages.');

      // If we are currently on a local environment, let's inform our fellow developers that they can mock the client messaging.
      if (isLocal) {
        logger.log('If you want to mock the client messaging, you can set the `NEXT_PUBLIC_TRACKER_MOCK_MESSAGE_CLIENT` environment variable to `true`. This will set up a message listener that will log the messages to the console instead of sending them to the client.');
      }

      // If the client cannot receive messages, we don't need to set up the listener.
      return;
    }
    window.addEventListener(EVENT_CONSENT_CHANGED, handleConsentChange, true);
    logger.log('Client messaging was enabled because the client can receive messages.');
    return () => {
      window.removeEventListener(EVENT_CONSENT_CHANGED, handleConsentChange, true);
    };
  }, []);

  // No UI rendering is needed for this component.
  return null;
};